import type { SxProps, Theme } from '@mui/material/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { ReactNode, useMemo, useState } from 'react';
import { generatePath, Link as RouterLink } from 'react-router';

import { MultiProcessOfferDto, ProcessOfferItemDto } from '@/api/generated';
import { themeColors } from '@/config/theme/themeColors.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { formatDate } from '@/utils/formatDate.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { sortAndAugmentByChain } from '@/views/processes/utils/sortOffersByChain.util.ts';
import { ProcessMultiOfferGroupOfferRow } from '@/views/processesDetails/components/multiOffers/comp/ProcessMultiOfferGroupOfferRow.comp.tsx';

type Props = {
  defaultExpanded?: boolean;
  group: MultiProcessOfferDto;
  previewDrawerSx?: SxProps<Theme>;
  renderExtraActions?: (offer: ProcessOfferItemDto) => ReactNode;
};

export const ProcessMultiOfferGroupAccordion: React.FC<Props> = ({
  defaultExpanded = false,
  group,
  previewDrawerSx,
  renderExtraActions,
}) => {
  const { t } = useTranslationWithPrefix('views.process.multiOffer');
  const { t: tOffers } = useTranslationWithPrefix('views.processesDetails.offers');
  const [expanded, setExpanded] = useState(defaultExpanded);

  const augmentedOffers = useMemo(
    () => sortAndAugmentByChain(group.processOffers),
    [group.processOffers],
  );

  const groupPath = generatePath(RouteEnum.PROCESSES_DETAILS_MULTI_OFFER, {
    multiOfferId: group.groupId,
    processId: group.processId,
  });

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        '&:before': { display: 'none' },
        border: `1px solid ${themeColors.stroke}`,
        borderRadius: 1,
        boxShadow: 'none',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', flexWrap: 'wrap', width: '100%' }}
        >
          <Typography sx={{ flex: 1, fontWeight: 600 }} variant="f14Medium">
            {group.name}
          </Typography>
          <Typography sx={{ color: themeColors.greyDark }} variant="f12Regular">
            {t('details.totalNetPrice')}: {group.totalNetPrice?.toFixed(2) ?? '-'}
          </Typography>
          <Typography sx={{ color: themeColors.greyDark }} variant="f12Regular">
            {t('details.totalGrossPrice')}: {group.totalGrossPrice?.toFixed(2) ?? '-'}
          </Typography>
          <Typography sx={{ color: themeColors.greyDark }} variant="f12Regular">
            {group.offerPrintName ?? '-'}
          </Typography>
          <Typography sx={{ color: themeColors.greyDark }} variant="f12Regular">
            {t('details.validTo')}: {group.validTo ? formatDate(group.validTo) : '-'}
          </Typography>
          <Chip label={t('details.statusOptions.' + group.status)} size="small" />
          <Tooltip title={t('details.menu.edit')}>
            <IconButton
              component={RouterLink}
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
              size="small"
              to={groupPath}
              viewTransition
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {augmentedOffers.length === 0 ? (
          <Typography sx={{ color: themeColors.greyDark, py: 2 }} variant="f14Regular">
            {t('details.noOffers')}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tOffers('body.version')}</TableCell>
                <TableCell>{t('details.offerHead.name')}</TableCell>
                <TableCell>{t('details.offerHead.eventDateFrom')}</TableCell>
                <TableCell>{t('details.offerHead.eventDateTo')}</TableCell>
                <TableCell>{t('details.offerHead.noOfGuests')}</TableCell>
                <TableCell>{t('details.offerHead.creator')}</TableCell>
                <TableCell>{t('details.offerHead.status')}</TableCell>
                <TableCell align="right">{t('details.offerHead.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {augmentedOffers.map(offer => (
                <ProcessMultiOfferGroupOfferRow
                  key={offer.processOfferId}
                  previewDrawerSx={previewDrawerSx}
                  renderExtraActions={renderExtraActions}
                  row={offer}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
